import { Box, FormHelperText, Typography } from '@material-ui/core';
import { SGSForm, SGSFormInput, SGSIcon, SGSTooltip } from 'components';
import SGSFormAutoComplete from 'components/Form/AutoComplete';
import { makeUniqueAndSort } from 'core/utils/utils';
import { useUserPerimetreContext } from 'features/utilisateurs/contexts/UtilisateurPerimetreProvider';
import { PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../Utilisateurs.module.scss';
import { hasRole } from 'core/utils/roles';
import { useAppContext } from 'core/contexts';
import { Alert } from '@material-ui/lab';
import { useLocation } from 'react-router';

type Props = {
    fields: any;
    append: Function;
    remove: Function;
    getValues?: Function;
    watch?: Function;
    errors: any;
    trigger: Function;
    readOnly?: boolean;
};

export const FormCodeGestionnaire: React.FC<PropsWithChildren<Props>> = ({ fields, append, remove, getValues, watch, trigger, errors, readOnly = false }) => {
    const { t } = useTranslation();
    const { user } = useAppContext();
    const location = useLocation();
    const { perimetre, selectedSouscrips } = useUserPerimetreContext();

    const handleAdd = () => append({ id: '', codeGestionnaire: '', idSouscrip: '' });
    const selectedSouscripIds = useCallback(() => selectedSouscrips.map((e) => e.value), [selectedSouscrips]);

    const filteredPerimetre = perimetre.filter((item) => hasRole(user, ['ROLE_ADMIN']) || selectedSouscripIds().includes(item.codSouscrip));
    const souscripOpts = makeUniqueAndSort(filteredPerimetre.map((item) => ({ value: item.codSouscrip, label: item.souscrip })));
    const subscribersToExclude = getValues('codeGestionnaireMultiple').map((e) => e.id);
    const subscriverAvailable = souscripOpts.filter((e) => !subscribersToExclude.includes(e.value));

    const watchCodeGestionnaire = watch('codeGestionnaireMultiple');

    const showAddIcon = () => {
        return subscriverAvailable.length > 0 && !getValues('codeGestionnaireMultiple').some((e) => e.idSouscrip === '' || e.codeGestionnaire === '');
    };

    const iconAdd = () => {
        const handleClick = showAddIcon() ? handleAdd : trigger;
        const className = showAddIcon() ? styles.addIcon : styles.addIconDisabled;
        return <SGSIcon title="global.add" icon="fas fa-plus" className={className} onClick={() => handleClick()} />;
    };

    return (
        <>
            {location.pathname !== '/utilisateurs/modifier' && (
                <SGSForm.Row>
                    <SGSForm.Col md={3}></SGSForm.Col>
                    <SGSForm.Col md={6}>
                        <Alert severity="info">
                            <Typography className={'text-center'}>Cette étape est facultative</Typography>
                        </Alert>
                    </SGSForm.Col>
                </SGSForm.Row>
            )}
            <SGSForm.Row>
                <SGSForm.Col md={6}>
                    <SGSFormInput variant="outlined" label="utilisateur.information.nom" name="codeGestionnaire" uppercase disabled={readOnly} />
                </SGSForm.Col>
                <SGSForm.Col md={6}>
                    <Typography className="mt-1 mb-4 text-center">Code gestionnaire par défaut pour l'ensemble des souscripteurs</Typography>
                </SGSForm.Col>
            </SGSForm.Row>
            <SGSForm.Row>
                <SGSForm.Col md={10}>
                    {(fields?.length || hasRole(user, ['ROLE_ADMIN'])) > 0 && <Typography className="mt-2 mb-4 text-center">{t('utilisateur.codesGestionnaire.introduction')}</Typography>}
                </SGSForm.Col>
                <SGSForm.Col md={1}></SGSForm.Col>
                <SGSForm.Col md={1}>{!readOnly && <Box className="text-center mt-3">{iconAdd()}</Box>}</SGSForm.Col>
            </SGSForm.Row>
            <Box className=" mt-n3 text-center">
                {errors?.codeGestionnaireMultiple && (
                    <FormHelperText className="m-0 p-0 text-center" error>
                        {(errors.codeGestionnaireMultiple as any).message}
                    </FormHelperText>
                )}
            </Box>
            {fields.map((item, index) => {
                return (
                    <SGSForm.Row key={item.id}>
                        <SGSForm.Col md={7}>
                            {watchCodeGestionnaire && (
                                <SGSFormAutoComplete
                                    variant="outlined"
                                    name={`codeGestionnaireMultiple.${index}.idSouscrip`}
                                    label="utilisateur.codesGestionnaire.souscripteur"
                                    disabled={readOnly}
                                    options={subscriverAvailable}
                                    value={
                                        souscripOpts.filter((e) => {
                                            return e.value === getValues(`codeGestionnaireMultiple.${index}.idSouscrip`);
                                        })[0] || null
                                    }
                                />
                            )}
                        </SGSForm.Col>
                        <SGSForm.Col md={4}>
                            <SGSFormInput variant="outlined" label="utilisateur.codesGestionnaire.code" name={`codeGestionnaireMultiple.${index}.codeGestionnaire`} disabled={readOnly} />
                        </SGSForm.Col>
                        {!readOnly && (
                            <SGSForm.Col md={1}>
                                <Box className="pt-2">
                                    <SGSTooltip className="text-center" title={<Box>{t('global.delete')}</Box>}>
                                        <SGSIcon title="global.delete" icon="fas fa-trash" onClick={() => remove(index)} className={styles.deleteIcon} />
                                    </SGSTooltip>
                                </Box>
                            </SGSForm.Col>
                        )}
                    </SGSForm.Row>
                );
            })}
            {!readOnly && (
                <SGSForm.Row>
                    <SGSForm.Col md={12}>
                        <Box className="text-center">
                            <SGSTooltip className="text-center" title={<Box>{t('global.add')}</Box>}>
                                {iconAdd()}
                            </SGSTooltip>
                        </Box>
                    </SGSForm.Col>
                </SGSForm.Row>
            )}
        </>
    );
};
